import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { has, isEmpty, sortBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import SSCSelect from '../../shared/SSCSelect';
import { Edit, IconPlus, Swap } from '../../../assets/svg';
import { DeleteButton } from '../../shared';
import {
    getAllPlatforms,
    popup,
    updateAccountHCPlatformAPI,
    updateHiringClientName,
} from '../../../utils';
import { HiringClientSection } from './HiringClientSection';
import {
    EDIT_CUSTOMER as EDIT,
    MAX_LENGTH,
    QUERY_KEYS,
    REGEX,
    ROLES,
    ROUTES,
    SOURCE,
    SYMBOL,
} from '../../../constants';
import { EditFileNameModal } from '../../shared/Modal/EditFileNameModal';
import useWindowSize from '../../../hooks/useWindowSize';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';

export const ManagePlatformBox = ({ accountData, selectedTpaId }) => {
    const accountId = accountData?.account?.account_id;
    const queryClient = useQueryClient();
    const [realData, setRealData] = useState([]);
    const role = useSelector((s) => s.user.role);
    const [data, setData] = useState([]);
    const [editPlatform, setEditPlatform] = useState(false);
    const [deletedPlatformIds, setDeletedPlatformIds] = useState([]);
    const [tpa, setTpa] = useState('');
    const [platformBox, setPlatformBox] = useState(true);
    const [searchPlatform, setSearchPlatform] = useState('');

    const [addedPlatform, setAddedPlatform] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [hcDetails, setHcDetails] = useState({ name: '', id: '' });
    const { width } = useWindowSize();
    const navigate = useNavigate();

    const handlePlatformDelete = () => {
        if (tpa) {
            setData((pre) => pre.filter((pf) => pf.id !== tpa.id));
            const currentTpa = data.find((pf) => pf.id !== tpa.id);
            setTpa(currentTpa);
            setDeletedPlatformIds((prev) => [...prev, tpa.id]);
            selectedTpaId.current = currentTpa.id;
        } else {
            popup('error', EDIT.POPUP_MSG.PLEASE_SELECT_PLATFORM);
        }
    };

    const [debouncedPtSearch] = useDebouncedValue(searchPlatform, 500);
    const { data: ptList, isFetching } = useQuery({
        queryKey: [QUERY_KEYS.PLATFORMS, debouncedPtSearch],
        queryFn: async () => await getAllPlatforms({ search: debouncedPtSearch }),
        select: (data) =>
            sortBy(
                data?.data?.map((ele) => ({
                    value: ele,
                    label: ele.name,
                })),
                'label',
            ),
        enabled: editPlatform,
    });

    const handleAddPlatForm = () => {
        setEditPlatform(true);
        setPlatformBox(false);
    };

    const handleEdit = () => {
        setEditPlatform(true);
        setPlatformBox(true);
    };

    const handleClose = () => {
        setEditPlatform(false);
        queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.ACCOUNT_BY_ID, accountId],
        });
        setData((pre) => {
            return { ...pre, platformsWithHC: [...accountData.platformsWithHC] };
        });
    };
    const saveAddedPlatform = () => {
        if (addedPlatform) {
            const isPlatformExist = data.find((platform) => platform.id === addedPlatform.id);

            isPlatformExist
                ? popup('error', EDIT.POPUP_MSG.PLATFORM_EXIST)
                : (setData((pre) => {
                      return [...pre, addedPlatform];
                  }),
                  setTpa(addedPlatform),
                  (selectedTpaId.current = addedPlatform.id),
                  setPlatformBox(true),
                  setAddedPlatform(''));
        } else {
            popup('error', EDIT.POPUP_MSG.PLEASE_SELECT_PLATFORM);
        }
    };

    useEffect(() => {
        if (accountData && accountData?.platformsWithHC) {
            setData(accountData?.platformsWithHC);
            setRealData(accountData?.platformsWithHC);
            if (selectedTpaId?.current) {
                setTpa(
                    accountData?.platformsWithHC?.find((ele) => ele?.id === selectedTpaId?.current),
                );
            } else {
                selectedTpaId.current = accountData?.platformsWithHC?.[0]?.id;
                setTpa(accountData?.platformsWithHC?.[0]);
            }
        }
        return () => {
            setData([]);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData]);

    const updatePlatformCache = (newData, hcs) => {
        const cacheExist = !isEmpty(
            queryClient.getQueryData([QUERY_KEYS.ACCOUNT_BY_ID, accountId]),
        );
        // optimistic update
        if (cacheExist) {
            queryClient.setQueryData([QUERY_KEYS.ACCOUNT_BY_ID, accountId], (old) =>
                old
                    ? {
                          ...old,
                          data: {
                              ...old.data,
                              platformsWithHC: newData,
                              hiringClients: hcs,
                          },
                      }
                    : old,
            );
        } else {
            queryClient.invalidateQueries({
                queryKey: [[QUERY_KEYS.ACCOUNT_BY_ID, accountId]],
            });
        }
    };

    const updateHcNameMutation = useMutation({
        mutationFn: async (fileName) =>
            await updateHiringClientName({
                data: { name: fileName },
                id: hcDetails.id,
            }),
        onSuccess: (_, fileName) => {
            const newData = data.map((item) => {
                if (item?.id === tpa?.id) {
                    item.hiringClients.map((hc) => {
                        if (hc?.id === hcDetails?.id) {
                            hc.name = fileName;
                        }
                    });
                }
                return item;
            });
            const hcs = accountData.hiringClients.map((hc) => {
                if (hc?.id === hcDetails?.id) {
                    hc.name = fileName;
                }
                return hc;
            });
            updatePlatformCache(newData, hcs);
            popup('success', 'Hiring client name updated!');
            setShowEditModal(false);
            setHcDetails({});
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const updateHcGradeMutation = useMutation({
        mutationFn: async (fileName) =>
            await updateAccountHCPlatformAPI({
                data: { hiring_client_grade: fileName !== '' ? fileName : null },
                account_id: accountData?.account?.account_id,
                hiring_client_id: hcDetails?.id,
                platform_id: tpa?.id,
            }),
        onSuccess: (_, fileName) => {
            const newData = data.map((item) => {
                if (item?.id === tpa?.id) {
                    item.hiringClients.map((hc) => {
                        if (hc?.id === hcDetails?.id) {
                            hc.hiring_client_grade = fileName;
                        }
                    });
                }
                return item;
            });
            const hcs = accountData.hiringClients.map((hc) => {
                if (hc?.id === hcDetails?.id) {
                    hc.hiring_client_grade = fileName;
                }
                return hc;
            });
            updatePlatformCache(newData, hcs);
            popup('success', 'Hiring client grade updated!');
            setShowEditModal(false);
            setHcDetails({});
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    const renameHandler = ({ fileName }) => {
        if (has(hcDetails, 'name')) {
            updateHcNameMutation.mutate(fileName);
        }
        if (has(hcDetails, 'hiring_client_grade')) {
            updateHcGradeMutation.mutate(fileName);
        }
    };

    return (
        [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CSM].includes(role) && (
            <div className='tabs-set h-25'>
                <div className='d-flex justify-content-end '>
                    {!editPlatform && (
                        <div className='d-flex gap-2'>
                            <button
                                title='Edit'
                                id='action-btn'
                                onClick={handleEdit}
                                className='bg-transparent'
                            >
                                <Edit className='cursor-pointer' />
                            </button>
                            <button
                                title='Reorder Hiring Clients'
                                id='action-btn'
                                onClick={() => {
                                    navigate(ROUTES.ADMIN.CUSTOMER_HIRING_CLIENT_ORDER, {
                                        state: { accountData: accountData, tpa: tpa },
                                    });
                                }}
                                className='bg-transparent'
                            >
                                <Swap />
                            </button>
                        </div>
                    )}
                </div>
                <div className=''>
                    {/* Platform Section */}
                    <div className='d-flex gap-2 flex-column justify-content-between align-item-center ps-lg-2 pe-lg-2 '>
                        <div
                            className='d-flex justify-content-start  align-item-center gap-4 overflow-y-auto'
                            key={selectedTpaId?.current}
                        >
                            {data?.length ? (
                                <div className='d-flex w-100 justify-content-between align-item-center'>
                                    <div className='d-flex align-item-center gap-2 justify-content-start'>
                                        {data.map((platform, index) => (
                                            <span
                                                key={`${platform.id}-${index}`}
                                                className={`cursor-pointer ${platform.id === tpa.id ? 'ssc-primary-green-btn' : 'ssc-secondary-white-btn'} 
                                button-green-filled`}
                                                onClick={() => {
                                                    setTpa(platform);
                                                    selectedTpaId.current = platform.id;
                                                }}
                                            >
                                                {platform.name ? platform.name : ''}
                                                <sup>{SYMBOL.REGISTERED}</sup>
                                            </span>
                                        ))}
                                    </div>
                                    {tpa && editPlatform && (
                                        <DeleteButton
                                            height={20}
                                            width={20}
                                            handleClick={handlePlatformDelete}
                                            className='ms-4'
                                        />
                                    )}
                                </div>
                            ) : (
                                !editPlatform && (
                                    <span
                                        className='mt-4 vh-50 d-flex justify-content-center w-100'
                                        style={{ fontSize: '16px' }}
                                    >
                                        {EDIT.BUTTON_TEXT.NO_PLATFORM}
                                    </span>
                                )
                            )}
                        </div>
                        {editPlatform && (
                            <div
                                className={`${!platformBox ? 'w-100' : ''} d-flex justify-content-start align-item-center gap-3 `}
                            >
                                {platformBox ? (
                                    <div className='d-flex align-items-center w-100 justify-content-between   '>
                                        <button
                                            onClick={handleAddPlatForm}
                                            className='ssc-primary-green-btn button-green-filled'
                                            style={{ margin: '4px 0' }}
                                        >
                                            {EDIT.BUTTON_TEXT.ADD_NEW_PLATFORM}
                                        </button>
                                    </div>
                                ) : (
                                    <div className='d-flex gap-2 gap-sm-4 flex-wrap align-items-center w-100 pt-lg-3 '>
                                        <SSCSelect
                                            placeholder={'Select Platforms'}
                                            options={ptList}
                                            value={
                                                !isEmpty(addedPlatform)
                                                    ? {
                                                          value: addedPlatform,
                                                          label: addedPlatform.name,
                                                      }
                                                    : null
                                            }
                                            onInputChange={setSearchPlatform}
                                            onChange={(d) => setAddedPlatform(d?.value)}
                                            formatOptionLabel={({ label }) => {
                                                return (
                                                    <div className='text-txt'>
                                                        <span>{label}</span>
                                                        <sup>{SYMBOL.REGISTERED}</sup>
                                                    </div>
                                                );
                                            }}
                                            isClearable
                                            className={'w-50'}
                                            isLoading={isFetching}
                                        />
                                        <div className='d-flex align-items-center gap-2 gap-sm-4'>
                                            {width > 768 ? (
                                                <button
                                                    onClick={saveAddedPlatform}
                                                    className='ssc-primary-green-btn rounded-1'
                                                    style={{
                                                        minWidth: 'max-content',
                                                        fontSize: '16px',
                                                    }}
                                                    title='Add Platform'
                                                >
                                                    Add Platform
                                                </button>
                                            ) : (
                                                <span
                                                    id='action-btn-green'
                                                    onClick={saveAddedPlatform}
                                                >
                                                    <IconPlus />
                                                </span>
                                            )}
                                            <span
                                                onClick={() => {
                                                    setPlatformBox(true);
                                                }}
                                                className='btn-close ms-lg-5 p-1'
                                                title='Close'
                                            ></span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {/* Hiring Client Section if edit */}
                    {editPlatform && (
                        <HiringClientSection
                            handleClose={handleClose}
                            id={accountId}
                            tpa={tpa}
                            realData={realData}
                            hiringClientData={data}
                            setHiringClientData={setData}
                            deletedPlatformIds={deletedPlatformIds}
                            setDeletedPlatformIds={setDeletedPlatformIds}
                        />
                    )}
                    {/* Hiring Client Section if no edit */}
                    {tpa && !editPlatform && (
                        <div
                            className='border user-create-form mt-2'
                            style={{ margin: 0 }}
                            key={tpa?.id}
                        >
                            <div className={`scroll-manage-hiring-clients`} id='wrapper'>
                                {tpa?.hiringClients?.length ? (
                                    <table className='table'>
                                        <thead style={{ zIndex: 99 }}>
                                            <tr>
                                                <th className='fw-normal lh-1 text-secondary pb-2 px-2 '>
                                                    {width > 720 ? 'Order' : '#'}
                                                </th>
                                                <th className='fw-normal lh-1 text-secondary pb-2 px-2 text-nowrap sticky-th-left'>
                                                    Hiring Client Name
                                                </th>
                                                <th className='fw-normal lh-1 text-secondary pb-2 px-2'>
                                                    Grade
                                                </th>
                                                <th className='fw-normal lh-1 text-secondary pb-2 px-2 text-nowrap'>
                                                    Created By
                                                </th>
                                                <th className='fw-normal lh-1 text-secondary pb-2 px-2'></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tpa.hiringClients?.map((hc, index) => {
                                                return (
                                                    <tr key={hc.id}>
                                                        <td className='edit-box-row'>
                                                            {index + 1}.
                                                        </td>
                                                        <td className='sticky-td-left'>
                                                            {hc.name}
                                                        </td>
                                                        <td className='d-flex gap-2'>
                                                            <span>
                                                                {hc.hiring_client_grade || null}
                                                            </span>
                                                            <button
                                                                title={
                                                                    hc.hiring_client_grade
                                                                        ? 'Edit Grade'
                                                                        : 'Add Grade'
                                                                }
                                                                id='action-icon'
                                                                onClick={() => {
                                                                    setHcDetails({
                                                                        hiring_client_grade:
                                                                            hc.hiring_client_grade,
                                                                        id: hc.id,
                                                                    });
                                                                    setShowEditModal(true);
                                                                }}
                                                                className='bg-transparent'
                                                            >
                                                                {hc.hiring_client_grade ? (
                                                                    <Edit className='cursor-pointer' />
                                                                ) : (
                                                                    <IconPlus
                                                                        className='cursor-pointer'
                                                                        stroke='#8993A4'
                                                                        height={18}
                                                                        width={18}
                                                                        style={{ margin: '2px' }}
                                                                    />
                                                                )}
                                                            </button>
                                                        </td>
                                                        <td>
                                                            {hc.is_from_salesforce ? (
                                                                <span className='text-danger'>
                                                                    {SOURCE.SCHEDULAR}
                                                                </span>
                                                            ) : (
                                                                SOURCE.CLOUD
                                                            )}
                                                        </td>
                                                        <td>
                                                            {!hc.is_from_salesforce && (
                                                                <span
                                                                    className='p-2'
                                                                    style={{ cursor: 'pointer' }}
                                                                    title='Rename Hiring Client'
                                                                    onClick={() => {
                                                                        setHcDetails({
                                                                            name: hc.name,
                                                                            id: hc.id,
                                                                        });
                                                                        setShowEditModal(true);
                                                                    }}
                                                                >
                                                                    <Edit />
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                ) : (
                                    <span className='text-special text-center ms-5'>
                                        {EDIT.BUTTON_TEXT.NO_HC}
                                    </span>
                                )}
                            </div>
                            <EditFileNameModal
                                key={hcDetails.id}
                                showEditModal={showEditModal}
                                setShowEditModal={setShowEditModal}
                                modalTitle={
                                    hcDetails.name
                                        ? 'Rename Hiring Client'
                                        : 'Update Hiring Client Grade'
                                }
                                inputLabel={
                                    hcDetails.name ? 'Hiring Client Name' : 'Hiring Client Grade'
                                }
                                inputPlaceholder={
                                    hcDetails.name
                                        ? 'Enter Hiring Client Name'
                                        : 'Enter Hiring Client Grade'
                                }
                                currentFileName={hcDetails.name || hcDetails.hiring_client_grade}
                                onClose={() => setHcDetails({})}
                                onSubmit={renameHandler}
                                isRequired={hcDetails.name ? true : false}
                                nameRegex={REGEX.ALL_LANG_NAME}
                                nameLengthLimit={MAX_LENGTH.HIRING_CLIENT_GRADE}
                            />
                        </div>
                    )}
                </div>
            </div>
        )
    );
};

ManagePlatformBox.propTypes = {
    accountData: PropTypes.object,
    setReload: PropTypes.func,
};
